import React, { useState, useMemo } from 'react';

export function useFilters(data, filterKeys) {
  const [filters, setFiltersState] = useState({});

  const setFilter = (key, value) => {
    setFiltersState(prev => ({ ...prev, [key]: value }));
  };

  const filtered = useMemo(() => {
    return data.filter(row =>
      filterKeys.every(key => {
        const val = filters[key];
        if (!val) return true;
        return String(row[key]) === val;
      })
    );
  }, [data, filters, filterKeys]);

  return { filters, setFilter, filtered };
}

export function FilterRow({ columns, data, filters, setFilter, labelMaps = {} }) {
  return (
    <tr>
      {columns.map((key, i) => (
        <th key={i} style={{ padding: '4px 8px' }}>
          {key ? (
            <select
              value={filters[key] || ''}
              onChange={e => setFilter(key, e.target.value)}
              style={{ width: '100%', fontSize: 12, padding: '2px 4px' }}
            >
              <option value="">Todos</option>
              {[...new Set(data.map(r => r[key]).filter(v => v != null && v !== ''))].sort().map(v => (
                <option key={v} value={String(v)}>{labelMaps[key]?.[v] ?? v}</option>
              ))}
            </select>
          ) : null}
        </th>
      ))}
    </tr>
  );
}

export function useSortedPaginated(data, defaultColKey = null, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultColKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const [page, setPage] = useState(0);
  const [perPage, setPerPageState] = useState(25);

  const setSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const setPerPage = (n) => {
    setPerPageState(n);
    setPage(0);
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      const numA = typeof aVal === 'number' ? aVal : Number(aVal);
      const numB = typeof bVal === 'number' ? bVal : Number(bVal);
      if (!isNaN(numA) && !isNaN(numB) && aVal !== '' && bVal !== '') {
        return sortDir === 'asc' ? numA - numB : numB - numA;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDir]);

  const total = sorted.length;
  const safePage = total === 0 ? 0 : Math.min(page, Math.ceil(total / perPage) - 1);
  const paginated = sorted.slice(safePage * perPage, (safePage + 1) * perPage);

  return { paginated, sortKey, sortDir, setSort, page: safePage, setPage, perPage, setPerPage, total };
}

export function SortableTh({ children, colKey, sortKey, sortDir, onSort, style }) {
  const isActive = colKey && colKey === sortKey;
  return (
    <th
      style={{ cursor: colKey ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={colKey ? () => onSort(colKey) : undefined}
    >
      {children}
      {colKey && (
        <span className={`sort-icon${isActive ? ' sort-active' : ''}`}>
          {isActive ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
        </span>
      )}
    </th>
  );
}

export function Pagination({ page, perPage, total, onPage, onPerPage }) {
  if (total <= 10) return null;
  const start = total === 0 ? 0 : page * perPage + 1;
  const end = Math.min((page + 1) * perPage, total);
  return (
    <div className="pagination-bar">
      <label className="pagination-per-page">
        Filas por página:
        <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </label>
      <span className="pagination-info">{start}–{end} de {total}</span>
      <button className="pagination-btn" onClick={() => onPage(page - 1)} disabled={page === 0}>‹</button>
      <button className="pagination-btn" onClick={() => onPage(page + 1)} disabled={end >= total}>›</button>
    </div>
  );
}
